import { mergeRight, zipObj, defaultTo, map, assoc, path, prop, clamp, isEmpty, mergeDeepRight, pick, assocPath } from 'ramda';
import { Functional as F, LatLng, Ordinal } from 'vms-base';
import { LandscapeState } from 'vms-state';
// TEMP!!
import * as MapNotesReducer from '../../vms-client/src/backend/reducers/map-notes-reducer.js';
import * as MapApi from './map-api.js';
import airportMapStyle from './style-airport.js';
// ---------------------------------------------------------------------------------------------------------------------
//
// CONSTANTS
//
// ---------------------------------------------------------------------------------------------------------------------
// animation time for Mapbox
const TRANSITION_DURATION = 500;
const { VENUE_LOADING_FINISHED } = LandscapeState.actions;
// workaround for Mapbox restriction that a "match" expression must have at least one element to match--
// but we sometimes want to "match" nothing if there's no Entity to highlight
const SELECT_NO_ENTITIES = ['this "id" will not match any entity'];
// ---------------------------------------------------------------------------------------------------------------------
//
// ACTIONS
//
// ---------------------------------------------------------------------------------------------------------------------
const DESELECT_ALL_FEATURES = 'DESELECT_ALL_FEATURES';
const SELECT_FEATURES_FOR_ENTITIES = 'SELECT_FEATURES_FOR_ENTITIES';
const SHOW_ORDINAL = 'SHOW_ORDINAL';
const SHOW_ORDINAL_WITH_CENTER_AND_ZOOM = 'SHOW_ORDINAL_WITH_CENTER_AND_ZOOM';
const UPDATE_VIEWPORT = 'UPDATE_VIEWPORT';
const ZOOM_IN = 'ZOOM_IN';
const ZOOM_OUT = 'ZOOM_OUT';
const RESET_NORTH = 'RESET_NORTH';
const SHOW_LAYER_ERRORS = 'SHOW_LAYER_ERRORS';
const WAYFINDING = 'WAYFINDING';
// ---------------------------------------------------------------------------------------------------------------------
//
// ACTION CREATORS
//
// ---------------------------------------------------------------------------------------------------------------------
/*
 * Construct a FUNCTION that takes arguments to use as an Action Creator
 * @sig def :: [String] -> ([*] -> { type: String, String: * })
 *
 * So:                     def(ADD_FEATURES, 'features', 'ordinal', 'levelId')
 * Returns this function:  (features, ordinal, levelId) => ({ type: ADD_FEATURES, features, ordinal, levelId })
 *
 */
const def = (type, ...argNames) => (...args) => mergeRight({ type }, zipObj(argNames, args));
const deselectAllFeatures = def(DESELECT_ALL_FEATURES);
const selectFeaturesForEntities = def(SELECT_FEATURES_FOR_ENTITIES, 'entityIds');
const showOrdinal = def(SHOW_ORDINAL, 'ordinalId');
const showOrdinalWithCenterAndZoom = def(SHOW_ORDINAL_WITH_CENTER_AND_ZOOM, 'ordinal', 'latitude', 'longitude', 'zoom');
const updateViewport = def(UPDATE_VIEWPORT, 'viewport');
const zoomIn = def(ZOOM_IN);
const zoomOut = def(ZOOM_OUT);
const resetNorth = def(RESET_NORTH);
const showLayerErrors = def(SHOW_LAYER_ERRORS);
const wayfinding = def(WAYFINDING);
// -----------------------------------------------------------------------------------------------------------------
//
// ACTION CREATOR THUNKS
//
// -----------------------------------------------------------------------------------------------------------------
//  initialize ---------------------------------------------------------------------------------------------------------
/*
 * Dispatches
 * Sets global.map for debugging
 * @sig setMapboxMap :: (MapboxGL-map, ReactMapGLViewport) -> (Dispatch, GetState) -> ()
 *
 */
const setMapboxMap = (_mapboxMap, venueId, viewportChanges) => (dispatch, getState) => {
    global.mapboxMap = _mapboxMap;
    // if we click on 'venues' but still clicked on the same venue we don't want to change the viewport
    const hasLoaded = F.pathString('map.loaded', getState());
    // change the viewport if the map has not loaded (probably just the width and height)
    if (!hasLoaded)
        dispatch(updateViewportWithClampedBounds(venueId, viewportChanges));
};
//  zoom ---------------------------------------------------------------------------------------------------------------
/*
 * Zoom the map to show an Entity, changing the center, zoom and possibly ordinal. Optionally also selects the Entity
 * Dispatches
 * @sig (EntityId, Boolean) -> Dispatch -> ()
 */
const zoomToEntity = (entityId, andSelect = true) => (dispatch, getState) => {
    const position = LandscapeState.selectors.positionForEntity(entityId.replace(/#/, ''), getState());
    if (!position.levelId)
        return;
    const ordinal = LandscapeState.selectors.ordinalForLevel(position.levelId, getState());
    dispatch(showOrdinalFromIdOrNumberWithViewport(ordinal, position.lat, position.lng, 20));
    if (andSelect)
        dispatch(actions.selectFeaturesForEntities([entityId]));
};
const zoomToMapNote = (mapNote) => dispatch => {
    const { location } = mapNote;
    dispatch(showOrdinalFromIdOrNumberWithViewport(location.ordinalId, location.latitude, location.longitude, 20));
};
/**
 * Zoom to show the bounding box of the specified structure
 * Dispatches
 * @sig zoomToStructure :: StructureId -> (Dispatch, GetState) -> ()
 */
const zoomToStructure = (venueId, structureId) => (dispatch, getState) => {
    const state = getState();
    const boundingBox = LandscapeState.selectors.getBoundingBoxForStructure(structureId, state);
    const oldViewport = selectors.getViewport(state);
    const viewport = MapApi.zoomToBoundingBox(oldViewport, boundingBox, TRANSITION_DURATION);
    dispatch(updateViewportWithClampedBounds(venueId, viewport));
};
/*
 * Zoom the map to the location stored in the map or, failing that, the Venue's center
 * Also sets the minZoom to what WebMercatorViewport calculates from the venue bounds
 * @sig zoomToLocationOrVenueCenter :: (Location, VenueId) -> ()
 *      Location = object managed by redux-first-router
 */
const zoomToLocationOrVenueCenter = (venueId, location) => (dispatch, getState) => {
    const state = getState();
    const boundingBox = LandscapeState.selectors.getBoundingBoxForVenue(venueId, getState());
    const oldViewport = selectors.getViewport(state);
    const viewport = MapApi.zoomToBoundingBox(oldViewport, boundingBox, 0);
    const venue = LandscapeState.selectors.getVenue(venueId, state);
    const ordinals = LandscapeState.selectors.ordinalsForVenue(venueId, state);
    const defaultOrdinalNumber = Ordinal.getDefaultOrdinalForVenue(ordinals).ordinalNumber;
    const query = defaultTo({}, location.query);
    const n = x => (Number.isNaN(x - 0) ? null : x - 0);
    const ordinal = defaultTo(defaultOrdinalNumber, query.ordinal);
    const lat = defaultTo(venue.venueCenter[0], query.lat);
    const lng = defaultTo(venue.venueCenter[1], query.lng);
    const zoom = defaultTo(viewport.zoom, query.zoom);
    dispatch(updateViewportWithClampedBounds(venueId, { minZoom: viewport.zoom }));
    dispatch(showOrdinalFromIdOrNumberWithViewport(ordinal, n(lat), n(lng), n(zoom)));
};
const showOrdinalFromIdOrNumberWithViewport = (ordinalOrIdOrNumber, latitude, longitude, zoom) => (dispatch, getState) => {
    const ordinal = LandscapeState.selectors.getOrdinalFromOrdinalOrIdOrNumber(ordinalOrIdOrNumber, getState());
    dispatch(actions.showOrdinalWithCenterAndZoom(ordinal, latitude, longitude, zoom));
};
const updateViewportWithClampedBounds = (venueId, viewport) => (dispatch, getState) => {
    const venueBounds = LandscapeState.selectors.getBoundingBoxForBasemap(venueId, getState());
    const clampedViewport = MapApi.clampBounds(viewport, venueBounds);
    dispatch(updateViewport(clampedViewport));
};
//  show ---------------------------------------------------------------------------------------------------------------
/*
 * Show the ordinal implied by the levelId. Note this dispatching function returns a value!
 * Dispatches
 * @sig showLevel :: LevelId -> Dispatch -> ()
 */
const showLevel = levelId => (dispatch, getState) => dispatch(showOrdinal(LandscapeState.selectors.ordinalForLevel(levelId, getState()).id));
// ---------------------------------------------------------------------------------------------------------------------
//
// SELECTORS
//
// ---------------------------------------------------------------------------------------------------------------------
const featuresToSource = features => ({
    type: 'geojson',
    data: {
        type: 'FeatureCollection',
        features,
    },
});
/*
 * Add sources to the baseStyle. Each key in 'source' is a source id and each value is an array of Features
 * @sig addSourcesToBaseStyle :: (MapboxStyle, { [sourceId]: [Feature] }) -> MapboxStyle
 */
const addSourcesToBaseStyle = (baseStyle, sources) => {
    sources = map(featuresToSource, sources); // convert [Features] to source
    return assoc('sources', sources, baseStyle);
};
/*
 * Generate a new Mapbox MapStyle for use by ReactMapGL
 *
 * - starts from the default (or basic) MapStyle
 * - adds sources for the background and current ordinal (only)
 * - adds layers for the background and current ordinal (only)
 *
 * Because it's expensive for ReactMapGL to handle changes to the MapStyle, this function is memoized so
 * that the MapStyle is only recomputed if:
 *
 * - the LandscapeState changes (indicated by a change to LandscapeState.lastChangedAt)
 * - the ordinal changed (indicated by a change to the currentOrdinalId)
 * - the highlighted Entity changed (indicated by a change to selectedEntityIds)
 */
const _getMapStyle = masterState => {
    const LANDSCAPE_BACKGROUND = Ordinal.LANDSCAPE_BACKGROUND;
    const NO_ORDINAL = 'NO_ORDINAL';
    const { currentOrdinalId, selectedEntityIds } = masterState.map;
    // Use the current ordinal as the source for the current geojson features to show on the map
    const ordinal = LandscapeState.selectors.getOrdinal(currentOrdinalId, masterState, false);
    const ordinalSource = ordinal ? ordinal.id : NO_ORDINAL; // We need to have an ordinal source otherwise mapbox complains
    const initializeBaseStyle = LandscapeState.selectors.getMapStyle(masterState) || airportMapStyle;
    return addSourcesToBaseStyle(initializeBaseStyle(ordinalSource, selectedEntityIds), {
        [LANDSCAPE_BACKGROUND]: LandscapeState.selectors.featuresForBackground(),
        [ordinalSource]: LandscapeState.selectors.featuresForOrdinal(currentOrdinalId),
        [MapApi.NAV_LINE_SOURCE]: MapApi.navlineFeatures(masterState.map),
        [MapApi.MAP_NOTES_SOURCE]: MapNotesReducer.Selectors.getConvertedMapNoteletsAsGeoJson(masterState, ordinal),
    });
};
/*
 * Is there a trigger that would cause the map style to need to be recomputed?
 * @sig hasMapStyleChanged :: State -> Boolean
 */
const hasMapStyleChanged = masterState => {
    const { currentOrdinalId, selectedEntityIds, wayfinding } = masterState.map;
    const ordinal = getCurrentOrdinal(masterState);
    const lastChangedAt = LandscapeState.selectors.getLastChangedAt(masterState);
    const mapNoteGeoJson = MapNotesReducer.Selectors.getConvertedMapNoteletsAsGeoJson(masterState, ordinal);
    return {
        // map data changed
        lastChangedAt,
        // user did something to the map itself
        currentOrdinalId,
        selectedEntityIds,
        wayfinding,
        // user changed something about the map notes
        mapNoteGeoJson,
    };
};
// memoized _getMapStyle
const getMapStyle = F.memoizeOnceWithObject(hasMapStyleChanged, _getMapStyle);
// compute the level at the given LatLng :: (LatLng, State, Boolean) -> LevelIdOrUndefined
const levelAtLatLng = (masterState, venueId, latLng, pickOneAnyway = false) => LandscapeState.selectors.levelAtLatLng(venueId, latLng, masterState.map.currentOrdinalId, masterState, pickOneAnyway);
// compute the level at the center of the map :: () -> LevelId|undefined
const levelAtCenter = (venueId, state) => levelAtLatLng(state, venueId, LatLng.fromLatitudeAndLongitude(state.map.viewport));
const getMapLoaded = (venueId, state) => {
    const isLoaded = path(['map', 'loaded', venueId], state);
    return isLoaded === 'LOADED';
};
/*
 * Get the currentOrdinal
 */
const getCurrentOrdinal = masterState => LandscapeState.selectors.getOrdinal(masterState.map.currentOrdinalId, masterState, false);
const getCurrentOrdinalNumber = masterState => prop('ordinalNumber', getCurrentOrdinal(masterState));
// ---------------------------------------------------------------------------------------------------------------------
//
// REDUCER
//
// ---------------------------------------------------------------------------------------------------------------------
/*
 * Initial redux state
 */
const initialState = () => ({
    selectedEntityIds: SELECT_NO_ENTITIES,
    viewport: {
        zoom: 15,
        transitionDuration: 0,
        maxZoom: 22,
        minZoom: 15,
        width: 800,
        height: 600,
    },
    currentOrdinalId: undefined,
    wayfinding: {
        startLocation: '', // String
        endLocation: '', // String
        segments: [],
    },
    loaded: {},
});
/*
 * Merge values from action.viewport into the viewport used by ReactMapGL
 */
const handleUpdateViewport = (state, action) => F.mergeWithProp('viewport', action.viewport, state);
// Shared zoom change helper
const changeZoom = (delta) => (state) => {
    const { minZoom = 15, maxZoom = 22, zoom } = state.viewport;
    return assocPath(['viewport', 'zoom'], clamp(minZoom, maxZoom, zoom + delta), state);
};
const handleZoomIn = changeZoom(1);
const handleZoomOut = changeZoom(-1);
const handleResetNorth = (state) => {
    const viewport = {
        pitch: 0,
        bearing: 0,
        transitionDuration: TRANSITION_DURATION,
    };
    return F.mergeWithProp('viewport', viewport, state);
};
/*
 * Update the currentOrdinalId -- but only if necessary
 */
const setCurrentOrdinalId = (state, ordinalId) => (ordinalId === state.currentOrdinalId ? state : assoc('currentOrdinalId', ordinalId, state));
/*
 * Show an Ordinal using an OrdinalId
 */
const handleShowOrdinal = (state, action) => setCurrentOrdinalId(state, action.ordinalId);
// Zoom to the given position and ordinal by updating the currentOrdinalId and viewport
const handleShowOrdinalWithCenterAndZoom = (state, action) => {
    const viewport = {
        latitude: action.latitude,
        longitude: action.longitude,
        zoom: action.zoom,
    };
    if (!isEmpty(state.loaded))
        viewport.transitionDuration = TRANSITION_DURATION;
    return mergeDeepRight(state, {
        currentOrdinalId: action.ordinal.id,
        viewport,
    });
};
/**
 * Highlight all the features associated with each entityId by updating the selectedEntityIds
 */
const handleSelectFeaturesForEntities = (state, action) => assoc('selectedEntityIds', action.entityIds, state);
/**
 * Unhighlight all the features by resetting the selectedEntityIds
 */
const handleDeselectAllFeatures = (state) => assoc('selectedEntityIds', SELECT_NO_ENTITIES, state);
/*
 * Rotate through the available fake navigations
 * TODO: This "wayfinding" is complete nonsense data
 */
const handleWayfinding = (state) => assoc('wayfinding', MapApi.navigate(), state);
const handleShowLayerErrors = (state) => {
    MapApi.validateFeaturesOnLayer(global.mapboxMap.queryRenderedFeatures());
    return state;
};
/*
 * Zoom to the venueCenter when loading is finished
 */
const handleLoadingFinished = (state, action) => {
    const { latitude, longitude } = state.viewport;
    return {
        ...state,
        viewport: { ...state.viewport, latitude, longitude },
        loaded: { ...state.loaded, [action.venueId]: 'LOADED' },
    };
};
const handleReset = (state) => F.mergeWithProp('viewport', pick(['width', 'height'], state.viewport), initialState());
const reducer = (state = initialState(), action) => {
    switch (action.type) {
        case LandscapeState.actions.RESET:
            return handleReset(state);
        case LandscapeState.actions.PERFORM_VALIDATION:
            return handleShowLayerErrors(state);
        case DESELECT_ALL_FEATURES:
            return handleDeselectAllFeatures(state);
        case SELECT_FEATURES_FOR_ENTITIES:
            return handleSelectFeaturesForEntities(state, action);
        case SHOW_ORDINAL:
            return handleShowOrdinal(state, action);
        case UPDATE_VIEWPORT:
            return handleUpdateViewport(state, action);
        case ZOOM_IN:
            return handleZoomIn(state);
        case ZOOM_OUT:
            return handleZoomOut(state);
        case SHOW_ORDINAL_WITH_CENTER_AND_ZOOM:
            return handleShowOrdinalWithCenterAndZoom(state, action);
        case VENUE_LOADING_FINISHED:
            return handleLoadingFinished(state, action);
        case RESET_NORTH:
            return handleResetNorth(state);
        case SHOW_LAYER_ERRORS:
            return handleShowLayerErrors(state);
        case WAYFINDING:
            return handleWayfinding(state);
        default:
            return state;
    }
};
// ---------------------------------------------------------------------------------------------------------------------
//
// Export
//
// ---------------------------------------------------------------------------------------------------------------------
const selectors = {
    getMapStyle,
    getMapLoaded,
    getViewport: F.pathString('map.viewport'),
    getCurrentOrdinalNumber,
    levelAtLatLng,
    levelAtCenter,
    determineMapClickAction: (mode, event) => MapApi.determineMapClickAction(mode, event.features),
};
const actions = {
    selectFeaturesForEntities,
    deselectAllFeatures,
    resetNorth,
    setMapboxMap,
    showLayerErrors,
    wayfinding,
    showLevel,
    showOrdinal,
    showOrdinalWithCenterAndZoom,
    updateViewport,
    zoomIn,
    zoomOut,
    zoomToEntity,
    zoomToMapNote,
    zoomToStructure,
    zoomToLocationOrVenueCenter,
};
const thunks = {
    updateViewportWithClampedBounds,
};
export { actions, reducer, selectors, thunks };
